<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import invitationsHttpService, {
  type BatchRecipient,
} from '@/services/http/invitationsHttpService.ts'
import PhoneContact from '@/pages/registrations/PhoneContact.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()
const queryClient = useQueryClient()

// params типизированы как объединение всех роутов; для динамического [id] берём id явно.
const batchId = computed(() => String((route.params as Record<string, string>).id))

// Поллинг ~3с: после запуска рассылки прогресс воронки обновляется фоновой задачей.
const batchQuery = useQuery({
  queryKey: computed(() => ['admin', 'invitations', 'batch', batchId.value, locale.value]),
  queryFn: () => invitationsHttpService.getBatch(batchId.value),
  refetchInterval: 3000,
})

const batch = computed(() => batchQuery.data.value ?? null)
const funnel = computed(() => batch.value?.funnel ?? null)
const recipients = computed<BatchRecipient[]>(() => batch.value?.recipients ?? [])

// Клик-фильтр воронки: ключ выбранного бокса (null = все). Тоггл, как в «Документах».
const funnelFilter = ref<string | null>(null)

// Предикаты фильтрации получателей по этапу/статусу (коды совпадают со счётчиками воронки бэка).
const funnelPredicates: Record<string, (r: BatchRecipient) => boolean> = {
  sent: (r) => r.sendStatusCode === 'Sent',
  delivered: (r) => r.deliveryStatusCode === 'Delivered',
  clicked: (r) => r.clickedAt != null,
  registered: (r) => r.registeredAt != null,
  sendPending: (r) => r.sendStatusCode === 'Pending',
  sendFailed: (r) => r.sendStatusCode === 'Failed',
  sendSkipped: (r) => r.sendStatusCode === 'Skipped',
  deliveryUndelivered: (r) => r.deliveryStatusCode === 'Undelivered',
  deliveryRejected: (r) => r.deliveryStatusCode === 'Rejected',
  deliveryExpired: (r) => r.deliveryStatusCode === 'Expired',
  deliveryUnknown: (r) => r.deliveryStatusCode === 'Unknown',
}

// Получатели с учётом клик-фильтра воронки; null/'total' → все.
const filteredRecipients = computed<BatchRecipient[]>(() => {
  const key = funnelFilter.value
  const pred = key ? funnelPredicates[key] : null
  return pred ? recipients.value.filter(pred) : recipients.value
})

// Клик по боксу воронки: тоггл фильтра; «Всего» (total) сбрасывает.
function pickFunnel(key: string) {
  const target = key === 'total' ? null : key
  funnelFilter.value = funnelFilter.value === target ? null : target
}
function isFunnelActive(key: string): boolean {
  return key === 'total' ? funnelFilter.value === null : funnelFilter.value === key
}

// Основная воронка боксами. Цвет числа по логике этапа: отправлено — голубой (в пути),
// доставлено — бирюзовый (успех), клики — фиолетовый (вовлечённость), регистрации — зелёный (цель).
const mainFunnelBoxes = computed(() => {
  const f = funnel.value
  if (!f) return []
  return [
    { key: 'total', label: t('invitations.detail.funnel.total'), count: f.total, color: '' },
    {
      key: 'sent',
      label: t('invitations.detail.funnel.sent'),
      count: f.sendSent,
      color: 'text-sky-600 dark:text-sky-400',
    },
    {
      key: 'delivered',
      label: t('invitations.detail.funnel.delivered'),
      count: f.deliveryDelivered,
      color: 'text-teal-600 dark:text-teal-400',
    },
    {
      key: 'clicked',
      label: t('invitations.detail.funnel.clicked'),
      count: f.clicked,
      color: 'text-violet-600 dark:text-violet-400',
    },
    {
      key: 'registered',
      label: t('invitations.detail.funnel.registered'),
      count: f.registered,
      color: 'text-green-600 dark:text-green-400',
    },
  ]
})

// Проблемные под-статусы отправки/доставки — боксами (как воронка Документов), а не тегами.
const subStatusBoxes = computed(() => {
  const f = funnel.value
  if (!f) return []
  const box = (key: string, count: number, color: string) => ({
    key,
    label: t(`invitations.detail.funnel.${key}`),
    count,
    color,
  })
  const red = 'text-red-600 dark:text-red-400'
  const amber = 'text-amber-600 dark:text-amber-400'
  return [
    box('sendPending', f.sendPending, amber),
    box('sendFailed', f.sendFailed, red),
    box('sendSkipped', f.sendSkipped, 'text-surface-500'),
    box('deliveryUndelivered', f.deliveryUndelivered, red),
    box('deliveryRejected', f.deliveryRejected, red),
    box('deliveryExpired', f.deliveryExpired, red),
    box('deliveryUnknown', f.deliveryUnknown, amber),
  ]
})

const sendMutation = useMutation({
  mutationFn: () => invitationsHttpService.send(batchId.value),
  onSuccess: () => {
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('invitations.detail.toasts.enqueued'),
      life: 3000,
    })
    queryClient.invalidateQueries({ queryKey: ['admin', 'invitations', 'batch', batchId.value] })
  },
  onError: (error: unknown) => {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: error instanceof Error ? error.message : t('invitations.detail.toasts.enqueueFailed'),
      life: 5000,
    })
  },
})

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

type TagSeverity = 'success' | 'warn' | 'danger' | 'secondary' | 'info'

function sendSeverity(code: string | null): TagSeverity {
  switch (code) {
    case 'Sent':
      return 'success'
    case 'Failed':
      return 'danger'
    case 'Skipped':
      return 'secondary'
    default:
      return 'warn'
  }
}

function deliverySeverity(code: string | null): TagSeverity {
  switch (code) {
    case 'Delivered':
      return 'success'
    case 'Undelivered':
    case 'Rejected':
    case 'Expired':
      return 'danger'
    case 'Unknown':
      return 'warn'
    default:
      return 'secondary'
  }
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div class="flex flex-wrap items-center justify-between gap-2 w-full">
      <div class="flex flex-col gap-1">
        <RouterLink to="/invitations" class="text-sm text-primary">
          ← {{ t('invitations.detail.back') }}
        </RouterLink>
        <h1 class="text-xl font-bold">{{ batch?.fileName ?? t('invitations.detail.title') }}</h1>
        <span v-if="batch" class="text-sm text-surface-500">
          {{ batch.eventName ?? '—' }} · {{ formatDateTime(batch.createdAt) }}
        </span>
      </div>
      <!-- Кнопку «Отправить» прячем, если рассылка уже была (batch.sentAt) — повторная отправка не нужна. -->
      <Button
        v-if="batch && !batch.sentAt"
        :label="t('invitations.detail.send')"
        icon="pi pi-send"
        :loading="sendMutation.isPending.value"
        @click="sendMutation.mutate()"
      />
      <span v-else-if="batch?.sentAt" class="text-sm text-surface-500">
        {{ t('invitations.detail.sentAt') }}: {{ formatDateTime(batch.sentAt) }}
      </span>
    </div>

    <!-- Воронка -->
    <Card v-if="funnel">
      <template #title>{{ t('invitations.detail.funnel.title') }}</template>
      <template #content>
        <div class="flex flex-col gap-4">
          <!-- Основная воронка: клик по боксу фильтрует список получателей; «Всего» сбрасывает. -->
          <div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <button
              v-for="box in mainFunnelBoxes"
              :key="box.key"
              type="button"
              class="flex flex-col gap-1 rounded border p-3 text-left transition-colors cursor-pointer hover:border-primary"
              :class="
                isFunnelActive(box.key)
                  ? 'border-primary bg-primary/5'
                  : 'border-surface-200 dark:border-surface-700'
              "
              :data-test="`funnel-${box.key}`"
              @click="pickFunnel(box.key)"
            >
              <span class="text-xs text-surface-500">{{ box.label }}</span>
              <span class="text-2xl font-bold" :class="box.color">{{ box.count }}</span>
            </button>
          </div>

          <!-- Проблемные под-статусы — боксами; клик так же фильтрует получателей. -->
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <button
              v-for="s in subStatusBoxes"
              :key="s.key"
              type="button"
              class="flex flex-col gap-1 rounded border p-3 text-left transition-colors cursor-pointer hover:border-primary"
              :class="
                isFunnelActive(s.key)
                  ? 'border-primary bg-primary/5'
                  : 'border-surface-200 dark:border-surface-700'
              "
              :data-test="`funnel-sub-${s.key}`"
              @click="pickFunnel(s.key)"
            >
              <span class="text-xs text-surface-500">{{ s.label }}</span>
              <span class="text-2xl font-bold" :class="s.color">{{ s.count }}</span>
            </button>
          </div>
        </div>
      </template>
    </Card>

    <!-- Получатели (с учётом клик-фильтра воронки). -->
    <DataTable
      :value="filteredRecipients"
      :loading="batchQuery.isLoading.value"
      data-key="id"
      class="w-full"
    >
      <template #empty>
        <div class="text-sm text-surface-500 py-4">
          {{ t('invitations.detail.recipients.empty') }}
        </div>
      </template>

      <Column :header="t('invitations.detail.recipients.name')">
        <template #body="{ data }">{{ data.name ?? '—' }}</template>
      </Column>
      <Column :header="t('invitations.detail.recipients.phone')">
        <template #body="{ data }"><PhoneContact :phone="data.phone" /></template>
      </Column>
      <Column field="localeCode" :header="t('invitations.detail.recipients.locale')" />
      <Column field="inviteCode" :header="t('invitations.detail.recipients.inviteCode')" />
      <Column :header="t('invitations.detail.recipients.sendStatus')">
        <template #body="{ data }">
          <Tag
            :value="data.sendStatusName ?? data.sendStatusCode ?? '—'"
            :severity="sendSeverity(data.sendStatusCode)"
          />
        </template>
      </Column>
      <Column :header="t('invitations.detail.recipients.deliveryStatus')">
        <template #body="{ data }">
          <Tag
            :value="data.deliveryStatusName ?? data.deliveryStatusCode ?? '—'"
            :severity="deliverySeverity(data.deliveryStatusCode)"
          />
        </template>
      </Column>
      <Column :header="t('invitations.detail.recipients.sendError')">
        <template #body="{ data }">{{ data.sendError ?? '—' }}</template>
      </Column>
      <Column :header="t('invitations.detail.recipients.sentAt')">
        <template #body="{ data }">{{ formatDateTime(data.sentAt) }}</template>
      </Column>
      <Column :header="t('invitations.detail.recipients.clicked')">
        <template #body="{ data }">
          <span v-if="data.clickedAt">
            {{ formatDateTime(data.clickedAt) }}
            <span class="text-surface-500">(×{{ data.clickCount }})</span>
          </span>
          <span v-else>—</span>
        </template>
      </Column>
      <Column :header="t('invitations.detail.recipients.registeredAt')">
        <template #body="{ data }">{{ formatDateTime(data.registeredAt) }}</template>
      </Column>
    </DataTable>
  </section>
</template>
